import { useEffect, useState } from "react";

// GitHub Linguist colors
const LANG_COLORS = {
  Dart: "#00B4AB",
  JavaScript: "#f1e05a",
  TypeScript: "#3178c6",
  Python: "#3572A5",
  Swift: "#F05138",
  Kotlin: "#A97BFF",
  Java: "#b07219",
  HTML: "#e34c26",
  CSS: "#563d7c",
  "C++": "#f34b7d",
  C: "#555555",
  Shell: "#89e051",
  Dockerfile: "#384d54",
  Ruby: "#701516",
  Go: "#00ADD8",
};

const FALLBACK_COLOR = "#666";

export default function useGithubLanguages(owner, repo) {
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!owner || !repo) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchLanguages() {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `https://api.github.com/repos/${owner}/${repo}/languages`,
          {
            headers: {
              Accept: "application/vnd.github+json",
              Authorization: `Bearer ${process.env.REACT_APP_GITHUB_TOKEN}`,
              "X-GitHub-Api-Version": "2022-11-28",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch GitHub languages");
        }

        const data = await response.json();

        if (cancelled || !data || typeof data !== "object") {
          return;
        }

        const total = Object.values(data).reduce(
          (total, bytes) => total + bytes,
          0
        );

        if (!total) {
          setLanguages([]);
          return;
        }

        const parsed = Object.entries(data)
          .map(([name, bytes]) => ({
            name,
            pct: (bytes / total) * 100,
            color: LANG_COLORS[name] || FALLBACK_COLOR,
          }))
          .sort((a, b) => b.pct - a.pct);

        if (!cancelled) {
          setLanguages(parsed);
        }
      } catch (error) {
        if (!cancelled) {
          setError(error.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchLanguages();

    return () => {
      cancelled = true;
    };
  }, [owner, repo]);

  return {
    languages,
    loading,
    error,
  };
}